/**
 * Presentation-only raster cache for canvas/export sticker art. The DOM editor
 * overlay draws art via plain `<img>` (browser handles loading natively), but
 * the Canvas2D compose path (`creative-overlay-compose.ts`, used by both the
 * hybrid export and the 3D export) is synchronous and runs per frame — it
 * needs an already-decoded `HTMLImageElement` to `drawImage` from. This module
 * preloads and caches those images so `drawSticker` can look one up
 * synchronously and fall back to the procedural shape when it isn't ready yet.
 *
 * Browser-only — guards for no-DOM/happy-dom test environments. Shell-only:
 * never touches core or the SourcePack/scoring graph.
 */
import { STICKER_ART } from "./sticker-art.js";

const artImageCache = new Map<string, HTMLImageElement>();
let artPreloadPromise: Promise<void> | null = null;

/** Safety-net cap per image so a caller (e.g. `exportReel`, which awaits this)
 *  can never hang forever — a stalled/never-firing load (broken cache, a
 *  test/no-network environment where `Image` never dispatches load/error,
 *  etc.) just times out and the caller falls back to the procedural shape
 *  for that sticker, same as a real decode failure. */
const PRELOAD_TIMEOUT_MS = 500;

/** Preload (and cache) the given image srcs. Idempotent — already-cached or
 *  already-pending srcs are skipped. Resolves once every src has settled
 *  (loaded, errored, or timed out); a failed/stalled load just leaves that
 *  src un-ready, so callers fall back to the procedural draw for it. */
export function preloadStickerArt(srcs: string[]): Promise<void> {
  if (typeof document === "undefined" || typeof Image === "undefined") {
    return Promise.resolve();
  }
  const pending: Promise<void>[] = [];
  for (const src of srcs) {
    if (artImageCache.has(src)) continue;
    const img = new Image();
    artImageCache.set(src, img);
    pending.push(
      new Promise<void>((resolve) => {
        let done = false;
        const finish = (): void => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve();
        };
        img.onload = finish;
        img.onerror = finish; // fall back to procedural on failure
        const timer = setTimeout(finish, PRELOAD_TIMEOUT_MS);
        img.src = src;
      }),
    );
  }
  artPreloadPromise = Promise.all(
    [artPreloadPromise, ...pending].filter(Boolean) as Promise<unknown>[],
  ).then(() => undefined);
  return artPreloadPromise;
}

/** Preload every sticker art asset in the shell's id→src catalog. */
export function preloadAllStickerArt(): Promise<void> {
  return preloadStickerArt(Object.values(STICKER_ART));
}

/** Loaded, decodable image for src, or null (caller falls back to procedural). */
export function readyStickerArt(src: string): HTMLImageElement | null {
  const img = artImageCache.get(src);
  if (img && img.complete && img.naturalWidth > 0) return img;
  return null;
}
